import { ROSELoader } from './rose';
import { BinaryReader } from '../util/binaryreader';
import { BinaryWriter } from '../util/binarywriter';

const ROSE_POSITION_SCALE_IN = 0.01;
const ROSE_POSITION_SCALE_OUT = 100;

export interface RawZONBlock {
  type: number;
  data: Uint8Array;
}

export class ZoneSpawnPoint {
  position: any = null;
  name: string = '';
}

export class ZoneTile {
  layer1: number = 0;
  layer2: number = 0;
  offset1: number = 0;
  offset2: number = 0;
  blend: boolean = false;
  rotation: number = 0;
  type: number = 0;
}

/**
 * @enum {Number}
 * @readonly
 */
export const BLOCK = {
  INFO:         0,
  SPAWN_POINTS: 1,
  TEXTURES:     2,
  TILES:        3,
  ECONOMY:      4
};

/**
 * @enum {Number}
 * @readonly
 */
export const TILE_ROTATION = {
  NONE:                 0,
  FLIP_HORIZONTAL:      2,
  FLIP_VERTICAL:        3,
  FLIP_BOTH:            4,
  CLOCKWISE_90:         5,
  COUNTER_CLOCKWISE_90: 6
};

/**
 * @property {String} name
 * @property {Number} type
 * @property {Number} width
 * @property {Number} height
 * @property {Number} gridCount
 * @property {Number} gridSize
 * @property {Number} startX
 * @property {Number} startY
 * @property {Boolean} underground
 * @property {String} backgroundMusicPath
 * @property {String} skyPath
 * @property {ZoneSpawnPoint[]} spawns
 * @property {ZoneTile[]} tiles
 * @property {String[]} textures
 */
export class ZoneData {
  // Static references to enums for backwards compatibility
  static BLOCK = BLOCK;
  static TILE_ROTATION = TILE_ROTATION;

  name: string = '';
  type: number = 0;
  width: number = 0;
  height: number = 0;
  gridCount: number = 0;
  gridSize: number = 0;
  startX: number = 0;
  startY: number = 0;
  underground: boolean = false;
  backgroundMusicPath: string = '';
  skyPath: string = '';
  spawns: ZoneSpawnPoint[] = [];
  tiles: ZoneTile[] = [];
  textures: string[] = [];
  sourceBlockOrder: number[] = [];
  rawBlocks: RawZONBlock[] = [];

  static parse(rh: BinaryReader): ZoneData {
    const data = new ZoneData();
    const blockCount = rh.readUint32();
    const headers: Array<{ type: number; offset: number }> = [];
    for (let index = 0; index < blockCount; index++) {
      headers.push({ type: rh.readUint32(), offset: rh.readUint32() });
    }

    const payloadEnd = (headerIndex: number): number => {
      const offset = headers[headerIndex].offset;
      let end = rh.byteLength;
      for (const header of headers) {
        if (header.offset > offset && header.offset < end) end = header.offset;
      }
      return end;
    };

    for (let blockIndex = 0; blockIndex < headers.length; blockIndex++) {
      const { type, offset } = headers[blockIndex];
      data.sourceBlockOrder.push(type);
      rh.seek(offset);

      let count: number;
      switch(type) {
      case BLOCK.INFO:
        data.type      = rh.readUint32();
        data.width     = rh.readUint32();
        data.height    = rh.readUint32();
        data.gridCount = rh.readUint32();
        data.gridSize  = rh.readFloat();
        data.startX    = rh.readUint32();
        data.startY    = rh.readUint32();
        break;
      case BLOCK.SPAWN_POINTS:
        count = rh.readUint32();
        for (let index = 0; index < count; ++index) {
          const spawn = new ZoneSpawnPoint();
          spawn.position = rh.readVector3().multiplyScalar(ROSE_POSITION_SCALE_IN);
          spawn.name     = rh.readUint8Str();
          data.spawns.push(spawn);
        }
        break;
      case BLOCK.TEXTURES:
        count = rh.readUint32();
        for (let index = 0; index < count; ++index) {
          data.textures.push(rh.readUint8Str());
        }
        break;
      case BLOCK.TILES:
        count = rh.readUint32();
        for (let index = 0; index < count; ++index) {
          const tile = new ZoneTile();
          tile.layer1   = rh.readUint32();
          tile.layer2   = rh.readUint32();
          tile.offset1  = rh.readUint32();
          tile.offset2  = rh.readUint32();
          tile.blend    = rh.readUint32() !== 0;
          tile.rotation = rh.readUint32();
          tile.type     = rh.readUint32();
          data.tiles.push(tile);
        }
        break;
      case BLOCK.ECONOMY:
        data.name                = rh.readUint8Str();
        data.underground         = rh.readUint32() !== 0;
        data.backgroundMusicPath = rh.readUint8Str();
        data.skyPath             = rh.readUint8Str();
        break;
      default:
        data.rawBlocks.push({ type, data: rh.slice(offset, payloadEnd(blockIndex)) });
        break;
      }
    }
    return data;
  }

  static serialize(data: ZoneData): ArrayBuffer {
    const knownBlocks = new Map<number, Uint8Array>();

    let writer = new BinaryWriter();
    writer.writeUint32(data.type);
    writer.writeUint32(data.width);
    writer.writeUint32(data.height);
    writer.writeUint32(data.gridCount);
    writer.writeFloat(data.gridSize);
    writer.writeUint32(data.startX);
    writer.writeUint32(data.startY);
    knownBlocks.set(BLOCK.INFO, writer.toUint8Array());

    if (
      data.spawns.length > 0 ||
      data.sourceBlockOrder.includes(BLOCK.SPAWN_POINTS)
    ) {
      writer = new BinaryWriter();
      writer.writeUint32(data.spawns.length);
      for (const spawn of data.spawns) {
        const position = spawn.position || { x: 0, y: 0, z: 0 };
        writer.writeVector3({
          x: position.x * ROSE_POSITION_SCALE_OUT,
          y: position.y * ROSE_POSITION_SCALE_OUT,
          z: position.z * ROSE_POSITION_SCALE_OUT,
        });
        writer.writeUint8Str(spawn.name || '');
      }
      knownBlocks.set(BLOCK.SPAWN_POINTS, writer.toUint8Array());
    }

    if (
      data.textures.length > 0 ||
      data.sourceBlockOrder.includes(BLOCK.TEXTURES)
    ) {
      writer = new BinaryWriter();
      writer.writeUint32(data.textures.length);
      for (const texture of data.textures) writer.writeUint8Str(texture);
      knownBlocks.set(BLOCK.TEXTURES, writer.toUint8Array());
    }

    if (
      data.tiles.length > 0 ||
      data.sourceBlockOrder.includes(BLOCK.TILES)
    ) {
      writer = new BinaryWriter();
      writer.writeUint32(data.tiles.length);
      for (const tile of data.tiles) {
        writer.writeUint32(tile.layer1);
        writer.writeUint32(tile.layer2);
        writer.writeUint32(tile.offset1);
        writer.writeUint32(tile.offset2);
        writer.writeUint32(tile.blend ? 1 : 0);
        writer.writeUint32(tile.rotation);
        writer.writeUint32(tile.type);
      }
      knownBlocks.set(BLOCK.TILES, writer.toUint8Array());
    }

    writer = new BinaryWriter();
    writer.writeUint8Str(data.name || '');
    writer.writeUint32(data.underground ? 1 : 0);
    writer.writeUint8Str(data.backgroundMusicPath || '');
    writer.writeUint8Str(data.skyPath || '');
    knownBlocks.set(BLOCK.ECONOMY, writer.toUint8Array());

    const rawQueues = new Map<number, Uint8Array[]>();
    for (const block of data.rawBlocks) {
      const queue = rawQueues.get(block.type) || [];
      queue.push(block.data);
      rawQueues.set(block.type, queue);
    }

    const blocks: Array<{ type: number; payload: Uint8Array }> = [];
    const emittedKnown = new Set<number>();
    for (const type of data.sourceBlockOrder) {
      const raw = rawQueues.get(type)?.shift();
      if (raw) {
        blocks.push({ type, payload: raw });
      } else if (!emittedKnown.has(type) && knownBlocks.has(type)) {
        blocks.push({ type, payload: knownBlocks.get(type)! });
        emittedKnown.add(type);
      }
    }
    for (const [type, payload] of knownBlocks) {
      if (!emittedKnown.has(type)) blocks.push({ type, payload });
    }
    for (const [type, queue] of rawQueues) {
      for (const payload of queue) blocks.push({ type, payload });
    }

    writer = new BinaryWriter();
    writer.writeUint32(blocks.length);
    const offsets: number[] = [];
    for (const block of blocks) {
      writer.writeUint32(block.type);
      offsets.push(writer.reserveUint32());
    }
    blocks.forEach((block, index) => {
      writer.patchUint32(offsets[index], writer.pos);
      writer.writeBytes(block.payload);
    });
    return writer.toArrayBuffer();
  }

  static load(path: any, callback: any) {
    ROSELoader.load(path, function(rh: any) {
      callback(ZoneData.parse(rh));
    });
  }
}
